import {
  LanguageLocaleEnum,
  Mandate,
  User,
  UserDocumentType,
  StatusEnum,
  MfaMethod,
  AccountingPspDocumentType,
  PayrollAccountingPspDocumentType,
  AttendanceMonth,
  Attendance,
  AttendanceDay,
  Giustificativo,
  Version,
  AvailableMonth,
  CampaignRecurrenceEnum,
  ReminderPopup,
  WeekDayEnum,
} from "../api/openapi";
import { TileArgs } from "react-calendar";
import React, { Dispatch, RefObject, SetStateAction } from "react";
import { PublicClientApplication } from "@azure/msal-browser";

export type MfaSetupState = "firstSetup" | "createNew" | "closed";

export type PspContextType = {
  initialized: boolean;
  lang: LanguageLocaleEnum;
  setLang: Dispatch<SetStateAction<LanguageLocaleEnum>>;
  user: User | undefined;
  setUser: Dispatch<SetStateAction<User | undefined>>;
  leftBarMobileOpen: boolean;
  setLeftBarMobileOpen: Dispatch<SetStateAction<boolean>>;
  selectedMandate: Mandate | undefined;
  setSelectedMandate: Dispatch<SetStateAction<Mandate | undefined>>;
  selectedMandateId: string | null;
  setSelectedMandateId: Dispatch<SetStateAction<string | null>>;
  setCookieConsentDialogOpen: Dispatch<SetStateAction<boolean>>;
  cookieConsentFunctionalOnly: boolean | undefined;
  setCookieConsentFunctionalOnly: Dispatch<SetStateAction<boolean | undefined>>;
  mfaSetupState: MfaSetupState;
  setMfaSetupState: Dispatch<SetStateAction<MfaSetupState>>;
  onMfaMethodCreatedRef:
    | RefObject<((method: MfaMethod) => void) | null>
    | undefined;
  msalInstance: PublicClientApplication | undefined;
  actorId: string | null;
  setActorId: Dispatch<SetStateAction<string | null>>;
};

export type AttendancesContextType = {
  attendanceMonth: AttendanceMonth | undefined;
  setAttendanceMonth: Dispatch<SetStateAction<AttendanceMonth | undefined>>;
  tempAttendanceMonth: TempAttendanceMonth | undefined;
  setTempAttendanceMonth: Dispatch<
    SetStateAction<TempAttendanceMonth | undefined>
  >;
  attendanceVersions: Version[] | undefined;
  setAttendanceVersions: Dispatch<SetStateAction<Version[] | undefined>>;
  selectedMonth: number | undefined;
  setSelectedMonth: Dispatch<SetStateAction<number | undefined>>;
  selectedYear: number | undefined;
  setSelectedYear: Dispatch<SetStateAction<number | undefined>>;
  availableMonths: AvailableMonth[] | undefined;
  setAvailableMonths: Dispatch<SetStateAction<AvailableMonth[] | undefined>>;
  isSubmitted: boolean | undefined;
  setIsSubmitted: Dispatch<SetStateAction<boolean | undefined>>;
  submittedAt: Date | undefined | null;
  setSubmittedAt: Dispatch<SetStateAction<Date | undefined | null>>;
  availableGiustificativi: Giustificativo[] | undefined;
  setAvailableGiustificativi: Dispatch<
    SetStateAction<Giustificativo[] | undefined>
  >;
};

export type CalendarTileContentFuncContextType = {
  tileContentFunc: (({ date }: TileArgs) => React.ReactNode) | null;
  setTileContentFunc: (fn: ({ date }: TileArgs) => React.ReactNode) => void;
};

export type IdType = number;

export type CollectionType = {
  id: string;
};

export type KeyLabelPair<T extends string | number = string> = {
  key: T;
  label: string;
};

export type TableColumn = KeyLabelPair & {
  className?: string;
};

export enum AccessEnum {
  AUTHORIZED = "authorized",
  UNAUTHORIZED = "unauthorized",
  LOADING = "loading",
}

export enum CookiePreferenceEnum {
  ESSENTIAL_ONLY = "essentialOnly",
  ALL = "all",
}

export const USER_DOCUMENT_TYPES = [
  "accounting",
  "payroll_accounting",
  "tax_return",
] as const satisfies UserDocumentType[];

export const ACCOUNTING_PSP_DOCUMENT_TYPES = [
  "ACCOUNTING_MWST_EINKAUFSREGISTER",
  "ACCOUNTING_MWST_VERKAUFSREGISTER",
  "ACCOUNTING_MWST_TAGESEINNAHMEN",
  "ACCOUNTING_MWST_ABRECHNUNG",
  "ACCOUNTING_MWST_JAHRESERKLAERUNG",
  "ACCOUNTING_MWST_RUECKERSTATTUNG",
  "ACCOUNTING_MWST_LIQUIDATION",
  "ACCOUNTING_JAHRESBILANZ",
  "ACCOUNTING_JAHRESBILANZ_VORJAHRESVERGLEICH",
  "ACCOUNTING_ZWISCHENBILANZ",
  "ACCOUNTING_ZWISCHENBILANZ_VORJAHRESVERGLEICH",
  "ACCOUNTING_EU_BILANZ",
  "ACCOUNTING_KONTENBLAETTER",
  "ACCOUNTING_JOURNAL",
  "ACCOUNTING_ABSCHREIBEREGISTER",
  "ACCOUNTING_OFFENE_POSTEN_KUNDEN",
  "ACCOUNTING_OFFENE_POSTEN_LIEFERANTEN",
  "ACCOUNTING_F24",
  "ACCOUNTING_ABSICHTSERKLAERUNGEN",
  "ACCOUNTING_INTRASTAT_MELDUNG",
  "ACCOUNTING_730",
  "ACCOUNTING_UNICO_PF",
  "ACCOUNTING_UNICO_SP",
  "ACCOUNTING_UNICO_SC",
  "ACCOUNTING_UNICO_ENC",
  "ACCOUNTING_CU",
  "ACCOUNTING_SONSTIGE",
] as const satisfies AccountingPspDocumentType[];

export const PAYROLL_ACCOUNTING_PSP_DOCUMENT_TYPES = [
  "PAYROLL_LOHNSTREIFEN",
  "PAYROLL_BUCHUNGSBELEG",
  "PAYROLL_BUCHUNGSBELEG_PRO_KOSTENSTELLE",
  "PAYROLL_BUCHUNGSBELEG_PRO_MITARBEITER",
  "PAYROLL_NETTOLISTE",
  "PAYROLL_URLAUBSUEBERSICHT",
  "PAYROLL_PFAENDUNGEN_FINANZIERUNGEN",
  "PAYROLL_GEWERKSCHAFTSBEITRAEGE",
  "PAYROLL_BLUTSPENDE",
  "PAYROLL_F24",
  "PAYROLL_EINZAHLUNGSSCHEIN_SANIFONDS",
  "PAYROLL_ABFERTIGUNGSFONDS",
  "PAYROLL_RUECKSTELLUNGEN",
  "PAYROLL_UEBERWEISUNGSFILE_LOEHNE",
  "PAYROLL_EINZAHLUNGSSCHEIN_BAUARBEITERKASSE",
  "PAYROLL_EINZAHLUNGSSCHEIN_CASAGIT",
  "PAYROLL_UEBERWEISUNGSFILE_ZUSATZRENTENFONDS",
  "PAYROLL_EFFEKTIVE_LOHNKOSTEN",
  "PAYROLL_ZAHLUNGSBESTAETIGUNG_F24",
  "PAYROLL_SONSTIGE",
] as const satisfies PayrollAccountingPspDocumentType[];

export type PspDocumentType =
  | AccountingPspDocumentType
  | PayrollAccountingPspDocumentType;

export const PSP_DOCUMENT_TYPES = [
  ...ACCOUNTING_PSP_DOCUMENT_TYPES,
  ...PAYROLL_ACCOUNTING_PSP_DOCUMENT_TYPES,
] as const satisfies PspDocumentType[];

export const STATUSES = [
  "gueltig",
  "wechsel",
  "uebernahme",
  "neueroeffnung",
  "schliessung",
  "abgelehnt",
  "abgeschlossen",
] as const satisfies StatusEnum[];

export const CAMPAIGN_RECURRENCES = [
  "once",
  "daily",
  "always",
] as const satisfies CampaignRecurrenceEnum[];

export type ReminderRecurrence = ReminderPopup["recurrence"]["recurrenceUnit"];

export const REMINDER_RECURRENCES = [
  "daily",
  "weekly",
  "monthly",
  "yearly",
] as const satisfies ReminderRecurrence[];

export const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const satisfies WeekDayEnum[];

export const LANGUAGE_LOCALES = [
  "de",
  "it",
  "en",
] as const satisfies LanguageLocaleEnum[];

export type PasswordPolicyError =
  | "too_short"
  | "missing_uppercase_letter"
  | "missing_lowercase_letter"
  | "missing_digit"
  | "missing_symbol";

export type TempAttendanceMonth = Omit<AttendanceMonth, "attendances"> & {
  attendances: TempAttendance[];
};

export type TempAttendance = Omit<Attendance, "days"> & {
  days: TempAttendanceDay[];
};

export type TempAttendanceDay = Omit<
  AttendanceDay,
  "actualHours" | "giustificativi"
> & {
  actualHours: string;
  giustificativi: TempGiustificativo[];
};

export type TempGiustificativo = Omit<Giustificativo, "hours"> & {
  hours: string;
};

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? DeepPartial<U>[]
    : T[P] extends object | undefined
      ? DeepPartial<T[P]>
      : T[P];
};
